import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useState } from "react";
import type { BookingPolicyResponse } from "./scheduleApi";

export interface PolicyFormProps {
  policy: BookingPolicyResponse;
  onSave: (body: BookingPolicyResponse) => Promise<{ ok: boolean; status?: number }>;
  onReload: () => void;
}

/**
 * The venue booking-policy form (§C.4): the venue-wide online-booking kill switch and THE canonical
 * self-service cancellation cutoff (deposits/cancellation-fee default to it — SC-6). Minutes in the UI;
 * a stale CAS surfaces the 409 reload prompt (§D.3).
 */
export function PolicyForm({ policy, onSave, onReload }: PolicyFormProps) {
  const { t } = useTranslation();
  const [online, setOnline] = useState(policy.onlineBookingEnabled);
  const [cutoff, setCutoff] = useState(policy.cancellationCutoffMinutes?.toString() ?? "");
  const [grace, setGrace] = useState(policy.gracePeriodMinutes.toString());
  const [noShow, setNoShow] = useState(policy.noShowPolicyText ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [conflict, setConflict] = useState(false);

  async function submit() {
    setSaving(true);
    setSaved(false);
    setConflict(false);
    try {
      const result = await onSave({
        ...policy,
        onlineBookingEnabled: online,
        cancellationCutoffMinutes: cutoff.trim() === "" ? null : Number.parseInt(cutoff, 10),
        gracePeriodMinutes: Number.parseInt(grace, 10) || 0,
        noShowPolicyText: noShow.trim() === "" ? null : noShow,
      });
      if (result.ok) setSaved(true);
      else if (result.status === 409) setConflict(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={css({ display: "flex", flexDirection: "column", gap: "3", maxW: "xl" })}>
      <h3 className={css({ fontSize: "lg", fontWeight: "semibold" })}>
        {t("schedule.policy.title")}
      </h3>

      {conflict ? (
        <div
          role="alertdialog"
          aria-label={t("schedule.conflict.title")}
          className={css({ p: "3", borderWidth: "1px", rounded: "md", bg: "bg.subtle" })}
        >
          <p className={css({ fontSize: "sm" })}>{t("schedule.conflict.message")}</p>
          <button
            type="button"
            onClick={onReload}
            className={css({ mt: "2", px: "3", py: "1", borderWidth: "1px", rounded: "md" })}
          >
            {t("schedule.conflict.reload")}
          </button>
        </div>
      ) : null}

      <label className={css({ display: "flex", gap: "2", alignItems: "center", fontSize: "sm" })}>
        <input type="checkbox" checked={online} onChange={(e) => setOnline(e.target.checked)} />
        {t("schedule.policy.onlineBooking")}
      </label>
      <label
        className={css({ display: "flex", flexDirection: "column", gap: "1", fontSize: "sm" })}
      >
        {t("schedule.policy.cancellationCutoff")}
        <input
          type="number"
          min={0}
          value={cutoff}
          onChange={(e) => setCutoff(e.target.value)}
          className={inputStyle}
        />
      </label>
      <label
        className={css({ display: "flex", flexDirection: "column", gap: "1", fontSize: "sm" })}
      >
        {t("schedule.policy.gracePeriod")}
        <input
          type="number"
          min={0}
          value={grace}
          onChange={(e) => setGrace(e.target.value)}
          className={inputStyle}
        />
      </label>
      <label
        className={css({ display: "flex", flexDirection: "column", gap: "1", fontSize: "sm" })}
      >
        {t("schedule.policy.noShowText")}
        <textarea
          value={noShow}
          onChange={(e) => setNoShow(e.target.value)}
          className={inputStyle}
          rows={2}
        />
      </label>

      <button
        type="button"
        onClick={() => void submit()}
        disabled={saving}
        className={css({
          alignSelf: "flex-start",
          px: "4",
          py: "2",
          rounded: "md",
          bg: "colorPalette.default",
          color: "colorPalette.fg",
        })}
      >
        {saving ? t("schedule.policy.saving") : t("schedule.policy.save")}
      </button>
      {saved ? (
        <output className={css({ display: "block", fontSize: "sm", color: "fg.muted" })}>
          {t("schedule.policy.saved")}
        </output>
      ) : null}
    </section>
  );
}

const inputStyle = css({ px: "3", py: "2", borderWidth: "1px", rounded: "md" });
